import { alpha, Box, Button, Grid, Paper, Typography, Table, TableBody, TableRow, TableCell, Tooltip, Snackbar, TableContainer } from '@mui/material'
import { useLocation, useParams } from 'react-router-dom'
import { usePostRequest } from '../../hooks/postRequest'
import { useCallback, useEffect, useState } from 'react'
import { useAppSelector } from '../../hooks'
import { PostType } from '../../types/types'
import theme from '../../utils/theme'
import GoBackBtn from '../../components/GoBackBtn'
import { CopyToClipboard } from 'react-copy-to-clipboard';
import FileOpenIcon from '@mui/icons-material/FileOpen';
import Loading from '../../components/Loading'
import { getRelativeTime } from '../../hooks/getRelativeTime'
import { ADMIN_BASE_URL, PUBLIC_BASE_URL } from '../../utils/config'

const PostDetail = () => {
  const { id } = useParams()
  const { pathname, search } = useLocation()
  const { loading } = useAppSelector(state => state.post);
  const postRequest = usePostRequest()
  const [ post, setPost ] = useState<PostType | null>(null)
  const [copied, setCopied] = useState<boolean>(false);
  const baseUrl = "https://career.cmj.vc/jobs"

  const handleCopy = useCallback(() => {
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const dispatchData = await postRequest({ baseUrl: pathname.includes('admin') ? ADMIN_BASE_URL : PUBLIC_BASE_URL, method: 'GET', data: null, id: Number(id), perPage: 12 });
      if(dispatchData.meta.requestStatus === "fulfilled"){
        pathname.includes('admin') ? setPost(dispatchData.payload.data) : setPost(dispatchData.payload.data.post)
      }
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    }
  }, [postRequest, id, pathname]);

  useEffect(() => {
    fetchData();
    window.scrollTo(0,0)
  }, [search, fetchData]);

  const isExpired: boolean | null = post && new Date(post.deadLine) < new Date();

  return (
    <Paper elevation={2} sx={{ p: { xs: 2, sm: 3, md: 4 }, borderRadius: '8px' }}>
      {
        loading && <Loading/>
      }
      {
        post &&
          <Grid container spacing={2}>
            <Grid item xs={12} lg={8} xl={8}>
             <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', fontStyle: 'italic' }}>Job Detail</Typography>
                  {isExpired ?
                    <Typography color="error" sx={{ fontStyle: 'italic', fontSize: '18px' }}>This job post has expired</Typography>
                    :
                    <Typography sx={{ fontStyle: 'italic', fontSize: '16px' }} color={theme.palette.secondary.contrastText}>{getRelativeTime(post.date)}</Typography>
                  }
                </Box>
              </Box>
              <Typography sx={{ fontStyle: 'italic', textDecoration: 'underline' }}>Job Title</Typography>
              <Typography sx={{ mb: 2, fontFamily: 'NotoSan' }} variant="h6">
                {post.title}
              </Typography>
              <Typography sx={{ mb: 2, fontStyle: 'italic', textDecoration: 'underline' }}>Job Category</Typography>
              <Typography sx={{ padding: '5px 10px', width: 'fit-content', backgroundColor: alpha(theme.palette.primary.main, 0.3), color: theme.palette.primary.main, fontSize: { xs: '12px', md: '16px' }, borderRadius: '15px' }}>{post.category}</Typography>
              <Typography sx={{ mt: 3, fontStyle: 'italic', textDecoration: 'underline' }}>Job Description</Typography>
              <Typography sx={{ mt: 1, mx: 1, mb: 2 }} dangerouslySetInnerHTML={{ __html: post.description }} />
            </Grid>
            <Grid item xs={12} lg={4} xl={4}>
              <Box sx={{ mt: { xs: 3, md: 0 }}}>
                <a href={post.googleFormLink} target='_blink'>
                  <Button fullWidth variant='contained' startIcon={<FileOpenIcon/>} disabled={isExpired ? isExpired : false}>Apply for this job</Button>
                </a>
              </Box>
              <TableContainer>
                <Table sx={{ mt: 3 }}>
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ py: 2 }}>
                        <Typography sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>ဗီဇာအမျိုးအစား :</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography>{post.visaType}</Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ py: 2 }}>
                        <Typography sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>ခေါ်ယူမည့်ဦးရေ :</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography>{post.infoOfNumOfWorkers}</Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ py: 2 }}>
                        <Typography sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>နေရာ :</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography>{post.location}</Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ py: 2 }}>
                        <Typography sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>လစာ :</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography>{post.salary} - Yen</Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ py: 2 }}>
                        <Typography sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>DeadLine :</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography>{new Date(post.deadLine).toLocaleDateString()}</Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ py: 2 }}>
                        <Typography sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Job URL:</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2, wordBreak: 'break-all' }}>
                        <CopyToClipboard text={`${baseUrl}/${id}`} onCopy={handleCopy}>
                          <Tooltip title="Copy link to clipboard" arrow>
                            <Typography sx={{ textDecoration: 'underline', fontSize: '12px', cursor: 'copy' }}>{`${baseUrl}/${id}`}</Typography>
                          </Tooltip>
                        </CopyToClipboard>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
      }
      <GoBackBtn/>
      <Snackbar
        open={copied}
        message="Link copied to clipboard!"
        autoHideDuration={3000}
        onClose={() => setCopied(false)}
      />
    </Paper>
  )
}

export default PostDetail
